import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
} from '@/components/ui/sheet';
import { EVENTS as TID } from '@/constants/testIds';

const ALL_VALUE = '__all__';

const ACCESS_OPTIONS = [
  { value: 'free', label: 'Gratis' },
  { value: 'premium', label: 'Premium' },
  { value: 'subscription', label: 'Suscripción' },
  { value: 'pay_per_event', label: 'Pago individual' },
];

function StyledSelect({ value, onChange, placeholder, options, testId, disabled = false }) {
  return (
    <Select
      value={value || ALL_VALUE}
      onValueChange={(v) => onChange(v === ALL_VALUE ? '' : v)}
      disabled={disabled}
    >
      <SelectTrigger
        className="h-11 rounded-xl bg-white border-[var(--ns-border)] text-sm focus:ring-[var(--ns-gold)]/40"
        data-testid={testId}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL_VALUE}>{placeholder}</SelectItem>
        {options.map((opt) => (
          <SelectItem
            key={opt.value || opt}
            value={String(opt.value ?? opt)}
          >
            {opt.label ?? opt}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FiltersInner({
  filters,
  setFilter,
  sports,
  clubs,
  countries,
  regions,
  provinces,
  cities,
  clearAll,
  hasAny,
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)]">Deporte</Label>
        <StyledSelect
          value={filters.sport}
          onChange={(v) => setFilter('sport', v)}
          placeholder="Todos los deportes"
          options={sports.map((s) => ({ value: s.slug, label: s.name }))}
          testId={TID.sportFilter}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)]">Club</Label>
        <StyledSelect
          value={filters.club}
          onChange={(v) => setFilter('club', v)}
          placeholder="Todos los clubes"
          options={clubs.map((c) => ({ value: c.slug, label: c.name }))}
          testId={TID.clubFilter}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)]">Tipo de acceso</Label>
        <StyledSelect
          value={filters.access_type}
          onChange={(v) => setFilter('access_type', v)}
          placeholder="Cualquier acceso"
          options={ACCESS_OPTIONS}
          testId={TID.accessTypeFilter}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)]">País</Label>
        <StyledSelect
          value={filters.country}
          onChange={(v) => setFilter('country', v)}
          placeholder="Todos los países"
          options={countries}
          testId={TID.countryFilter}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)]">Comunidad / Región</Label>
        <StyledSelect
          value={filters.region}
          onChange={(v) => setFilter('region', v)}
          placeholder="Todas las comunidades"
          options={regions}
          testId={TID.regionFilter}
          disabled={regions.length === 0}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)]">Provincia</Label>
        <StyledSelect
          value={filters.province}
          onChange={(v) => setFilter('province', v)}
          placeholder="Todas las provincias"
          options={provinces}
          testId={TID.provinceFilter}
          disabled={provinces.length === 0}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--ns-text-subtle)]">Ciudad</Label>
        <StyledSelect
          value={filters.city}
          onChange={(v) => setFilter('city', v)}
          placeholder="Todas las ciudades"
          options={cities}
          testId={TID.cityFilter}
          disabled={cities.length === 0}
        />
      </div>
      {hasAny ? (
        <Button
          type="button"
          variant="outline"
          className="h-10 rounded-xl border-[var(--ns-border)] text-[var(--ns-text)] hover:bg-[var(--ns-bg-2)]"
          onClick={clearAll}
          data-testid={TID.clearFilters}
        >
          <X className="h-4 w-4 mr-2" /> Limpiar filtros
        </Button>
      ) : null}
    </div>
  );
}

export function EventFilters({
  query,
  setQuery,
  filters,
  setFilter,
  clearAll,
  sports,
  clubs,
  countries,
  regions,
  provinces,
  cities,
}) {
  const hasAny =
    !!query ||
    !!filters.sport ||
    !!filters.club ||
    !!filters.access_type ||
    !!filters.country ||
    !!filters.region ||
    !!filters.province ||
    !!filters.city;

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--ns-text-subtle)]" />
          <Input
            type="search"
            inputMode="search"
            placeholder="Buscar evento por título…"
            className="h-11 pl-10 pr-10 rounded-xl bg-white border-[var(--ns-border)] focus-visible:ring-[var(--ns-gold)]/40"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid={TID.searchInput}
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded-md text-[var(--ns-text-subtle)] hover:text-[var(--ns-text)] hover:bg-[var(--ns-bg-2)]"
              aria-label="Limpiar búsqueda"
              data-testid={TID.searchClear}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>

        <div className="sm:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-11 rounded-xl border-[var(--ns-border)] w-full"
                data-testid={TID.toggleFiltersMobile}
              >
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                Filtros
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full max-w-sm bg-white overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="font-display">Filtros</SheetTitle>
              </SheetHeader>
              <div className="py-4">
                <FiltersInner
                  filters={filters}
                  setFilter={setFilter}
                  sports={sports}
                  clubs={clubs}
                  countries={countries}
                  regions={regions}
                  provinces={provinces}
                  cities={cities}
                  clearAll={clearAll}
                  hasAny={hasAny}
                />
              </div>
              <SheetFooter />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="hidden sm:block mt-6">
        <div className="rounded-2xl border border-[var(--ns-border)] bg-white p-5">
          <FiltersInner
            filters={filters}
            setFilter={setFilter}
            sports={sports}
            clubs={clubs}
            countries={countries}
            regions={regions}
            provinces={provinces}
            cities={cities}
            clearAll={clearAll}
            hasAny={hasAny}
          />
        </div>
      </div>
    </>
  );
}
